import { AgGridReact } from 'ag-grid-react';
import { useEffect, useRef } from 'react';
import { type GridOptions, RowSelectionOptions, AllCommunityModule, GridApi } from 'ag-grid-community';
import {
	Tab,
	ScreenMode,
	AuditGridProps
} from '../../../common/domain/mappingAudit';

const defaultColumnDef = () => ({
  resizable: true,
  sortable: true,
  filter: true,
  editable: false,
  filterParams: {
    comparator: (a: string, b: string) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
  },
});

const formatOperationLabel = (op?: string) => {
  switch ((op ?? '').toUpperCase()) {
    case 'INSERT':
      return 'Add';
    case 'UPDATE':
		  return 'Change';
	  case 'DELETE':
      return 'Delete';
    default:
      return op ?? '';
  }
};

const formatBigQueryTimestamp = (value?: string) => {
  if (!value) return '';

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};



export const ratesAuditColumnDefs = [
  { headerName: 'Id', field: 'id', hide: true },

  { headerName: 'Group ID', field: 'groupId' },
  { headerName: 'Field', field: 'fieldName' },

  { headerName: 'Input Value', field: 'inputValue' },
  { headerName: 'Mapped Value', field: 'mappedValue' },

  { headerName: 'Valid From', field: 'validFrom', valueFormatter: (params) => formatBigQueryTimestamp(params.value) },
  { headerName: 'Valid To', field: 'validTo', valueFormatter: (params) => formatBigQueryTimestamp(params.value) },

  { headerName: 'Updated By', field: 'updatedBy' },
  { headerName: 'Updated At', field: 'updateTimestamp', valueFormatter: (params) => formatBigQueryTimestamp(params.value) },

  { headerName: 'Operation', field: 'operationType', valueFormatter: (params: any) => formatOperationLabel(params.value) },
];

export const ratesFixingProxyColumnDefs = [
  { headerName: 'Id', field: 'id', hide: true },

  { headerName: 'Group ID', field: 'groupId' },

  { headerName: 'Input Reference Index', field: 'inputReferenceIndex' },
  { headerName: 'Mapped Reference Index', field: 'mappedReferenceIndex' },

  { headerName: 'Pay Frequency', field: 'mappedPayFrequency' },
  { headerName: 'Day Count', field: 'mappedDayCount' },

  { headerName: 'Valid From', field: 'validFrom', valueFormatter: (params) => formatBigQueryTimestamp(params.value) },
  { headerName: 'Valid To', field: 'validTo', valueFormatter: (params) => formatBigQueryTimestamp(params.value) },

  { headerName: 'Updated By', field: 'updatedBy' },
  { headerName: 'Updated At', field: 'updateTimestamp', valueFormatter: (params) => formatBigQueryTimestamp(params.value) },

  { headerName: 'Operation', field: 'operationType', valueFormatter: (params: any) => formatOperationLabel(params.value) },
];

export const legalEntityAuditColumnDefs = [
  { headerName: 'Id', field: 'id', hide: true },

  { headerName: 'Legal Entity', field: 'legalEntity' },
  { headerName: 'Mapped Group ID', field: 'mappedGroupId' },
  { headerName: 'Valid From', field: 'validFrom', valueFormatter: (params) => formatBigQueryTimestamp(params.value) },
  { headerName: 'Valid To', field: 'validTo', valueFormatter: (params) => formatBigQueryTimestamp(params.value) },

  { headerName: 'Updated By', field: 'updatedBy' },
  { headerName: 'Updated At', field: 'updateTimestamp', valueFormatter: (params) => formatBigQueryTimestamp(params.value) },

  { headerName: 'Operation', field: 'operationType', valueFormatter: (params: any) => formatOperationLabel(params.value) },
];

const gridOptions: GridOptions = {
  animateRows: true,
  accentedSort: true,
  suppressClickEdit: true,

};

export const AuditGrid = (props: AuditGridProps) => {
  const {
    rowData,
    isLoading,
    tab,
    screenMode,
    onGridReady,
    onSelectionChanged,
    onFilterChanged,
  } = {
    rowData: [],
    isLoading: false,
    ...props,
  };
  const apiRef = useRef<GridApi | null>(null);
  const columnDefsMap = {
    [Tab.RATES_FIELD]: ratesAuditColumnDefs,
    [Tab.RATES_FIXING]: ratesFixingProxyColumnDefs,
    [Tab.LEGAL_ENTITY]: legalEntityAuditColumnDefs,
  };

  const columnDefs = columnDefsMap[tab];

  useEffect(() => {
    const api = apiRef.current;
    if (!api || screenMode !== ScreenMode.SELECTING_MODE || !onFilterChanged) return;
    const onFilterChangedHandler = () => {
      setTimeout(() => {
        const currentApi = apiRef.current;
        if (currentApi) onFilterChanged(currentApi);
      }, 0);
    };
    api.addEventListener('filterChanged', onFilterChangedHandler);
    return () => api.removeEventListener('filterChanged', onFilterChangedHandler);
  }, [screenMode, onFilterChanged]);

  const rowSelection: RowSelectionOptions | undefined =
    screenMode === ScreenMode.SELECTING_MODE
      ? { mode: 'multiRow', selectAll: 'filtered' }
      : undefined;

  return (
    <div className="ag-theme-alpine" style={{ height: 600, width: '100%' }}>
		  <AgGridReact
			  gridOptions={ gridOptions}
        columnDefs={columnDefs}
  rowData={rowData}
  defaultColDef={defaultColumnDef()}
  rowSelection={rowSelection}
  suppressRowClickSelection={true}
  loading={isLoading}
			  modules={[AllCommunityModule]}
			  onGridReady={(params) => {
				  params.api.sizeColumnsToFit();
          apiRef.current = params.api;
				  onGridReady?.(params.api);
			  }}
			  onSelectionChanged={onSelectionChanged}
      />
    </div>
  );
};




